'use client';
import React, { useMemo } from 'react';
import { CalculationCardForDrawer } from '../../components/calculation-card-for-drawer';
import {
  Label,
  List,
  ListItem,
  RadioButtonGroup,
  RadioButton,
  Status,
} from '@yaradigitallabs/ahua-react';
import { formatDate, getClassNames } from '@/utils/helpers';
import { ManualCorrection } from '../../components/manual-correction';
import { useCalculationContext } from '../../context/calculation-context';
import {
  //MeasurementUnitEnum,
  ProductionReportMeasurementEntryEnum,
} from '../../utils/types';
import type {
  Material,
  MaterialComponent,
  MaterialRouting,
  MaterialWithSoftTag,
} from '@/features/match-maker/utils/match-maker.types';
import { fetchDefaultUnitFromMaterial } from '../../utils/helpers';

type CalculationListProps = {
  isLoading: boolean;
  error?: string | null;
  onDetailClick: (tag: MaterialWithSoftTag) => void;
  tagValues?: Record<
    string,
    {
      value: number;
      unit: string;
      timestamp: string;
      hasError?: boolean;
    }
  >;
  material: Material | MaterialComponent | MaterialRouting | null;
};

const CalculationList: React.FC<CalculationListProps> = ({
  isLoading,
  error,
  onDetailClick,
  tagValues,
}) => {
  const { material, setMeasurement, measurement, materialUnits } =
    useCalculationContext();
  const manualItem = `${material?.id || ''}_manual_correction`;
  const defaultUnit = useMemo(() => {
    if (!material) return '';
    return fetchDefaultUnitFromMaterial(material, materialUnits);
  }, [material, materialUnits]);

  const handleSelectCalculation = ({
    tagId,
    timestamp = '',
    value,
    unit,
  }: {
    tagId: string | null;
    timestamp?: string;
    value?: number | null;
    unit: string;
  }) => {
    setMeasurement(prev => ({
      ...prev,
      tag_id: tagId,
      quantity: value ?? 0,
      unit,
      timestamp: formatDate(timestamp),
      entry_type: tagId
        ? ProductionReportMeasurementEntryEnum.TAG
        : ProductionReportMeasurementEntryEnum.MANUAL,
    }));
  };

  const handleManualCorrectionChange = (value: number) => {
    setMeasurement(prev => ({
      ...prev,
      tag_id: null,
      entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
      quantity: value,
      unit: measurement.unit,
      timestamp: null,
    }));
  };

  return (
    <div>
      {error && (
        <Status size="n" colorType="red">
          {error}
        </Status>
      )}
      <RadioButtonGroup
        defaultValue={measurement.tag_id || manualItem}
        aria-label="Calculation Selection"
        className="[&>div]:gap-0!"
        value={measurement.tag_id || manualItem}
      >
        <List>
          {(material?.tags || []).map(tag => (
            <List.Middle
              className={getClassNames(
                'overflow-y-visible!',
                tag.id === measurement.tag_id ? 'bg-gray-100' : ''
              )}
              key={tag.id}
            >
              <ListItem css={{ padding: '0px' }}>
                <ListItem.BodyTail divider>
                  <ListItem.Body
                    css={{
                      paddingLeft: '0px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      flexDirection: 'row',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <RadioButton
                      value={tag.id}
                      id={tag.id}
                      concept="success"
                      onClick={() =>
                        handleSelectCalculation({
                          tagId: tag.id,
                          value: tagValues?.[tag.id]?.value ?? 0,
                          timestamp: tagValues?.[tag.id]?.timestamp ?? '',
                          unit: tagValues?.[tag.id]?.unit || defaultUnit,
                        })
                      }
                    />
                    <Label htmlFor={tag.id} className="w-full cursor-pointer">
                      <CalculationCardForDrawer
                        tag={tag}
                        tagValues={tagValues?.[tag.id]}
                        isLoading={isLoading}
                        onDetailSelect={() => onDetailClick(tag)}
                      />
                    </Label>
                  </ListItem.Body>
                </ListItem.BodyTail>
              </ListItem>
            </List.Middle>
          ))}
          <ListItem css={{ padding: '0px' }}>
            <ListItem.BodyTail divider>
              <ListItem.Body
                css={{
                  paddingLeft: '0px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <RadioButton
                  value={manualItem}
                  id={manualItem}
                  concept="success"
                  onClick={() =>
                    handleSelectCalculation({
                      tagId: null,
                      value: null,
                      unit: defaultUnit,
                    })
                  }
                />
                <Label htmlFor={manualItem} className="w-full cursor-pointer">
                  <ManualCorrection
                    id={manualItem}
                    value={
                      measurement.tag_id === null
                        ? (measurement.quantity ?? 0)
                        : 0
                    }
                    unit={defaultUnit}
                    onChange={handleManualCorrectionChange}
                    disabled={measurement.tag_id !== null}
                  />
                </Label>
              </ListItem.Body>
            </ListItem.BodyTail>
          </ListItem>
        </List>
      </RadioButtonGroup>
    </div>
  );
};

export default CalculationList;
